<template>
  <el-dialog
    title="Link Whatsapp Channel"
    width="500px"
    :visible.sync="showDialog"
    append-to-body>

    <div class="dialog-body" v-if="!whatsappChannelId && !isAuthed">
      <p>Please choose your country and enter your phone number.</p>
      <el-form label-width="120px">
        <el-form-item label="Country">
          <country-code-selector class="country-selector"
            :browserCountryCode="userCountryCode"
            :selectedCountry="selectedCountry.code"
            @onCountryChanged="handleCountryChanged">
          </country-code-selector>
        </el-form-item>
        <el-form-item label="Phone number">
          <el-input v-model="phoneNumber" placeholder="Phone number">
            <template slot="prepend">
              {{ selectedCountry.flag }}
              {{ selectedCountry.dial_code }}
            </template>
          </el-input>
          <small class="muted">Phone number without a leading <em>0 (zero)</em> or <em>spaces</em></small>
        </el-form-item>
      </el-form>
    </div>

    <div class="dialog-body text-center" v-if="whatsappChannelId && !isAuthed">
      <div v-if="!whatsappQRCode" class="loader">
        <social-icon class="fa fa-fw" :type="'WhatsApp'" />
      </div>
      <br/>
      <div class="loader-text" v-if="loadingWhatsappQRCodeRequest">
        Waiting for QR Code.
        <i class="fa fa-spinner fa-pulse"></i>
      </div>
      <div class="info-text" v-if="!loadingWhatsappQRCodeRequest && !whatsappQRCode">
        <h2>Please follow the instructions carefully.</h2>
        <p>On your mobile device within <strong>WhatsApp</strong>,
          go to Settings <strong>WhatsApp Web/Desktop</strong>
          <i class="fa fa-arrow-right"></i> <strong>Scan QR Code</strong>.
          Once your phone's camera is <strong>ready</strong>
          click the <em>Request QR Code</em> button below.</p>
        <p>When the QR code displays you will have around <strong>10 to 15 seconds</strong>
          to scan the code before it will time out automatically, if you're unable  to scan it
          within this time, you will return back to this screen and will be able to request a new
          QR code.</p>
        <p>Once your mobile device has been linked, this window will close automatically.
          Your mobile device will need to stay <strong>powered on</strong>
          and <strong>connected to the internet</strong> for your messages to be received by us.</p>
      </div>
      <div class="qrcode" v-if="whatsappQRCode">
        This QR code will expire in <strong>{{ expiryCounter }}</strong> seconds.
        <qrcode :text="whatsappQRCode" :size="460"></qrcode>
      </div>
    </div>

    <div class="dialog-body" v-if="isAuthed">
      <div class="loader">
        <social-icon class="fa fa-fw" :type="'WhatsApp'" />
      </div>
      <br/>
      <div class="info-text">
        <h2>Success.</h2>
        <p>Your account has been linked <strong>successfully</strong>,
          from time to time it may happen that
          WhatsApp <strong>expires</strong> your session, when this happens,
          simply follow this process of linking your number again.</p>
      </div>
    </div>

    <div class="text-danger">
      <small><em>Please note this feature is HIGHLY EXPERIMENTAL!</em></small>
    </div>

    <div slot="footer" class="dialog-footer" v-if="!whatsappChannelId && !isAuthed">
      <el-button @click="createChannel" :disabled="!validMobileNumber || loadingWhatsappCreateChannel" plain>
        <i class="fa fa-spin fa-spinner" v-if="loadingWhatsappCreateChannel"></i> Link
      </el-button>
    </div>

    <div slot="footer" class="dialog-footer" v-if="whatsappChannelId && !isAuthed">
      <el-button type="primary" @click="generateQRCode" plain v-if="!whatsappQRCode">Request QR Code</el-button>
      <el-button type="primary" @click="close()" plain v-if="whatsappQRCode">Accept</el-button>
    </div>

    <div slot="footer" class="dialog-footer" v-if="isAuthed">
      <el-button type="primary" @click="close()" plain>Accept</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';
import SocialIcon from '@/components/common/Icons/SocialIcon';
import VueQRCodeComponent from 'vue-qrcode-component';
import CountryCodeSelector from '@/components/common/countries/CountryCodeSelector';
import * as types from '@/store/modules/whatsapp/mutation-types';

export default {
  name: 'WhatsappChannelCreateDialog',
  components: { qrcode: VueQRCodeComponent, SocialIcon, CountryCodeSelector },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    intialData: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      selectedCountry: {
        flag: null,
        code: null,
        dial_code: '',
      },
      phoneNumber: '',
      expiryRunning: false,
      expiryCounter: 15,
      isAuthed: false,
      ...this.intialData,
    };
  },
  computed: {
    ...mapState('user', ['me', 'userCountryCode']),
    ...mapState('whatsapp', [
      'loadingWhatsappQRCodeRequest',
      'loadingWhatsappCreateChannel',
      'whatsappQRCode',
      'whatsappChannelId',
      'whatsappPollSuccess',
    ]),
    showDialog: {
      get() {
        return this.isVisible;
      },

      set(visibility) {
        if (!visibility) {
          this.resetState();
        }
        this.$emit('onVisibleChanged', visibility);
      },
    },
    validMobileNumber() {
      const mobileNumber = this.selectedCountry.dial_code + this.phoneNumber;
      return (
        /^[^0]/.test(this.phoneNumber) && /^\+\d+$/.test(mobileNumber) && mobileNumber.length >= 9
      );
    },
  },
  methods: {
    generateQRCode() {
      this.$store.commit(`whatsapp/${types.POLL_WHATSAPP_CHANNEL_STOP}`);
      this.$store.commit(`whatsapp/${types.POLL_WHATSAPP_CHANNEL_RESET}`);
      this.$store.commit(`whatsapp/${types.RESET_WHATSAPP_QRCODE}`);
      this.$store.dispatch('whatsapp/requestWhatsappQRCode', this.whatsappChannelId);
      this.$store.dispatch('whatsapp/pollWhatsappStatus', this.whatsappChannelId);
    },
    clearQRCode() {
      this.$store.commit(`whatsapp/${types.RESET_WHATSAPP_QRCODE}`);
    },
    resetState() {
      // Reset state and close
      this.expiryRunning = false;
      this.selectedCountry = {
        flag: null,
        code: null,
        dial_code: '',
      };
      this.expiryCounter = 15;
      this.isAuthed = false;
      this.phoneNumber = '';
      this.clearQRCode();
      this.$store.commit(`whatsapp/${types.RESET_WHATSAPP_CHANNEL_ID}`);
      this.$store.commit(`whatsapp/${types.POLL_WHATSAPP_CHANNEL_STOP}`);
      this.$store.commit(`whatsapp/${types.POLL_WHATSAPP_CHANNEL_RESET}`);
    },
    createChannel() {
      const mobileNumber = `${this.selectedCountry.dial_code}${this.phoneNumber}`;
      this.$store.dispatch('whatsapp/createWhatsappChannel', mobileNumber);
    },
    handleCountryChanged(country) {
      this.selectedCountry = country;
    },
    close() {
      this.resetState();
      this.showDialog = false;
    },
    qrCodeTimeoutRun() {
      setTimeout(() => {
        this.expiryCounter -= 1;
        if (this.expiryCounter === 0) {
          this.clearQRCode();
          this.expiryCounter = 15;
          this.$store.commit(`whatsapp/${types.POLL_WHATSAPP_CHANNEL_STOP}`);
          this.expiryRunning = false;
        }
        if (this.expiryCounter > 0 && this.expiryRunning) {
          this.qrCodeTimeoutRun();
        }
      }, 1000);
    },
  },
  watch: {
    whatsappPollSuccess(value) {
      if (value) {
        this.$emit('onWhatsappChannelAdded');
        this.isAuthed = true;
        this.expiryRunning = false;
      }
    },
    whatsappQRCode(value) {
      if (value) {
        this.expiryRunning = true;
        this.expiryCounter = 15;
        this.qrCodeTimeoutRun();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.loader {
  text-align: center;
  font-size: 3em;
}

.loader-text {
  text-align: center;
  font-size: 1.25em;
}

.text-danger {
  text-align: center;
}

.qrcode {
  width: 100%;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// WhatsappWebChannelCreateDialog.vue?a449910a